import { refresh } from "lib/orgServer/OrgServerClient";

export type ScheduledJob = {
    id: string;
    orgId: string;
    missionId: string;
    arcSerial: string;
    repeat: boolean;
    repeatInterval: number;
    repeatUnit: "MONTH" | "WEEK" | "DAY" | "HOUR" | "MINUTE";
    startsAt: string;
    nextRunAt: string | null;
    repeatUntil: string | null;
    timezone: string;
    maxDelaySeconds: number;
    enabled: boolean;
    createdAt: string;
    updatedAt: string;
};

export type ScheduledJobLog = {
    id: string;
    scheduledJobId: string;
    missionId: string;
    arcSerial: string;
    occurrenceAt: string;
    attemptedAt: string | null;
    dispatchStartedAt: string | null;
    triggeredAt: string | null;
    finishedAt: string | null;
    status: string;
    arcOnline: boolean | null;
    missionPresent: boolean | null;
    message: string | null;
    sourceRunId: string | null;
    goalId: string | null;
    createdAt: string;
};

export type ScheduledJobInput = {
    missionId: string;
    arcSerial: string;
    startsAt: string;
    repeat?: boolean;
    repeatInterval?: number;
    repeatUnit?: ScheduledJob["repeatUnit"];
    repeatUntil?: string | null;
    timezone?: string;
    maxDelaySeconds?: number;
    enabled?: boolean;
};

export const FLEET_SCHEDULE_GRAPHQL_PATH = "/api/fleet-mission-storage/graphql";

export const fleetScheduleGraphqlUrl = (proxyHostname: string | null | undefined): string | null => {
    const host = proxyHostname?.trim().replace(/^[a-z][a-z\d+.-]*:\/\//i, "").replace(/\/+$/, "");
    return host ? `https://${host}${FLEET_SCHEDULE_GRAPHQL_PATH}` : null;
};

const request = async <T>(
    proxyHostname: string | null | undefined,
    query: string,
    variables: Record<string, unknown> = {},
): Promise<T> => {
    const url = fleetScheduleGraphqlUrl(proxyHostname);
    if (!url) throw new Error("No fleet proxy configured");
    const make = () => fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(localStorage.getItem("orgUserToken")
                ? { Authorization: `Bearer ${localStorage.getItem("orgUserToken")}` }
                : {}),
        },
        body: JSON.stringify({ query, variables }),
        cache: "no-store",
        mode: "cors",
        credentials: "omit",
    });
    let response = await make();
    if (response.status === 401) {
        await refresh();
        response = await make();
    }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await response.json() as { data?: T; errors?: Array<{ message?: string; extensions?: { code?: string } }> };
    if (payload.errors?.length) {
        const first = payload.errors[0];
        throw new Error(`${first.extensions?.code ?? "GRAPHQL_ERROR"}: ${first.message ?? "request failed"}`);
    }
    return payload.data as T;
};

const JOB_FIELDS = `id orgId missionId arcSerial repeat repeatInterval repeatUnit startsAt nextRunAt repeatUntil timezone maxDelaySeconds enabled createdAt updatedAt`;
const LOG_FIELDS = `id scheduledJobId missionId arcSerial occurrenceAt attemptedAt dispatchStartedAt triggeredAt finishedAt status arcOnline missionPresent message sourceRunId goalId createdAt`;

export const listScheduledJobs = (proxy: string | null | undefined) => request<{ scheduledJobs: ScheduledJob[] }>(proxy,
    `query { scheduledJobs(includeDisabled: true) { ${JOB_FIELDS} } }`).then((data) => data.scheduledJobs);

export const listScheduledJobLogs = (proxy: string | null | undefined, scheduledJobId: string) => request<{ scheduledJobLogs: ScheduledJobLog[] }>(proxy,
    `query($id: ID!) { scheduledJobLogs(scheduledJobId: $id) { ${LOG_FIELDS} } }`, { id: scheduledJobId }).then((data) => data.scheduledJobLogs);

export const listScheduledJobLogsForRange = (
    proxy: string | null | undefined,
    occurrenceStart: string,
    occurrenceEnd: string,
) => request<{ scheduledJobLogs: ScheduledJobLog[] }>(proxy,
    `query($start: DateTime!, $end: DateTime!) { scheduledJobLogs(occurrenceStart: $start, occurrenceEnd: $end, limit: 500) { ${LOG_FIELDS} } }`,
    { start: occurrenceStart, end: occurrenceEnd },
).then((data) => data.scheduledJobLogs);

export const createScheduledJob = (proxy: string | null | undefined, input: ScheduledJobInput) => request<{ createScheduledJob: ScheduledJob }>(proxy,
    `mutation($input: CreateScheduledJobInput!) { createScheduledJob(input: $input) { ${JOB_FIELDS} } }`, { input }).then((data) => data.createScheduledJob);

export const updateScheduledJob = (proxy: string | null | undefined, id: string, input: Partial<ScheduledJobInput>) => request<{ updateScheduledJob: ScheduledJob }>(proxy,
    `mutation($id: ID!, $input: UpdateScheduledJobInput!) { updateScheduledJob(id: $id, input: $input) { ${JOB_FIELDS} } }`, { id, input }).then((data) => data.updateScheduledJob);

export const deleteScheduledJob = (proxy: string | null | undefined, id: string) => request<{ deleteScheduledJob: boolean }>(proxy,
    "mutation($id: ID!) { deleteScheduledJob(id: $id) }", { id }).then((data) => data.deleteScheduledJob);

export const getScheduledJobCount = (proxy: string | null | undefined) => request<{ scheduledJobCount: number }>(proxy,
    "query { scheduledJobCount(activeOnly: true) }").then((data) => data.scheduledJobCount);

export type SchedulePageEntity = "missions" | "scheduled_jobs" | "scheduled_job_logs" | "runs";

type SchedulePageSubscriptionPayload = {
    data?: { schedulePageChanged?: { entities?: SchedulePageEntity[] } };
    errors?: Array<{ message?: string }>;
};

/**
 * Subscribe through Strawberry's GraphQL-over-SSE transport.
 *
 * This intentionally uses authenticated fetch instead of EventSource or a
 * WebSocket: fetch can carry the organization bearer token through arc_proxy
 * without putting that token in a URL.
 */
export const subscribeSchedulePageChanges = (
    proxyHostname: string | null | undefined,
    scheduledJobId: string | null,
    onChange: (entities: SchedulePageEntity[]) => void,
    onError: (error: unknown) => void = console.error,
): (() => void) => {
    const url = fleetScheduleGraphqlUrl(proxyHostname);
    if (!url) return () => {};

    const controller = new AbortController();
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleReconnect = () => {
        if (controller.signal.aborted || reconnectTimer) return;
        reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            void connect();
        }, 2000);
    };

    const processEvent = (eventBlock: string) => {
        const lines = eventBlock.split(/\r?\n/);
        const event = lines.find((line) => line.startsWith("event:"))?.slice(6).trim();
        if (event !== "next") return;
        const data = lines
            .filter((line) => line.startsWith("data:"))
            .map((line) => line.slice(5).trimStart())
            .join("\n");
        if (!data) return;
        const payload = JSON.parse(data) as SchedulePageSubscriptionPayload;
        if (payload.errors?.length) throw new Error(payload.errors[0].message ?? "Schedule subscription failed");
        const entities = payload.data?.schedulePageChanged?.entities;
        if (entities?.length) onChange(entities);
    };

    const open = () => fetch(url, {
        method: "POST",
        headers: {
            Accept: "text/event-stream",
            "Content-Type": "application/json",
            ...(localStorage.getItem("orgUserToken")
                ? { Authorization: `Bearer ${localStorage.getItem("orgUserToken")}` }
                : {}),
        },
        body: JSON.stringify({
            query: `subscription($scheduledJobId: ID) {
                schedulePageChanged(scheduledJobId: $scheduledJobId) { entities }
            }`,
            variables: { scheduledJobId },
        }),
        cache: "no-store",
        mode: "cors",
        credentials: "omit",
        signal: controller.signal,
    });

    const connect = async () => {
        try {
            let response = await open();
            if (response.status === 401) {
                await refresh();
                response = await open();
            }
            if (!response.ok) throw new Error(`Schedule subscription HTTP ${response.status}`);
            if (!response.body) throw new Error("Schedule subscription response has no body");

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            while (!controller.signal.aborted) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                let boundary = buffer.search(/\r?\n\r?\n/);
                while (boundary >= 0) {
                    const block = buffer.slice(0, boundary);
                    const separator = buffer.slice(boundary).match(/^\r?\n\r?\n/)?.[0] ?? "\n\n";
                    buffer = buffer.slice(boundary + separator.length);
                    processEvent(block);
                    boundary = buffer.search(/\r?\n\r?\n/);
                }
            }
            scheduleReconnect();
        } catch (error) {
            if (controller.signal.aborted) return;
            onError(error);
            scheduleReconnect();
        }
    };

    void connect();
    return () => {
        controller.abort();
        if (reconnectTimer) clearTimeout(reconnectTimer);
    };
};
